import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const rotatingWords = ['Software', 'Platforms', 'Billing Systems', 'Cloud Products'];

const Carouselnew = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [inView, setInView] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => {
      setWordIndex(prev => (prev + 1) % rotatingWords.length);
    }, 2400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    const target = 12.4;
    const duration = 1200;
    const startTime = performance.now();
    let raf;
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setRevenue(+(eased * target).toFixed(1));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [inView]);

  return (
    <section className={`bh-hero ${inView ? 'is-in' : ''}`} ref={heroRef}>
      <style>{`
        .bh-hero {
          position: relative;
          background: #FBF8F2;
          padding: 7.5rem 2rem 5rem;
          overflow: hidden;
        }

        .bh-hairline-frame {
          position: absolute;
          inset: 2rem;
          border: 1px solid rgba(24,22,18,0.07);
          border-radius: 1.5rem;
          pointer-events: none;
          z-index: 0;
        }

        .bh-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          pointer-events: none;
          opacity: 0.5;
        }

        .bh-glow-1 {
          width: 460px;
          height: 460px;
          top: -12%;
          right: -8%;
          background: radial-gradient(circle, rgba(184,134,11,0.16) 0%, transparent 70%);
        }

        .bh-glow-2 {
          width: 380px;
          height: 380px;
          bottom: -16%;
          left: -10%;
          background: radial-gradient(circle, rgba(139,124,246,0.12) 0%, transparent 70%);
        }

        .bh-inner {
          position: relative;
          z-index: 2;
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          gap: 4.5rem;
        }

        .bh-text-col {
          flex: 1.05;
        }

        .bh-kicker {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.4rem 0.95rem 0.4rem 0.65rem;
          background: #FFFFFF;
          border: 1px solid rgba(184,134,11,0.25);
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 1.3px;
          text-transform: uppercase;
          color: #9A6B0C;
          box-shadow: 0 2px 8px rgba(24,22,18,0.04);
          opacity: 0;
          transform: translateY(10px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .bh-hero.is-in .bh-kicker {
          opacity: 1;
          transform: translateY(0);
        }

        .bh-kicker-mark {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #B8860B;
        }

        .bh-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: clamp(2.4rem, 4.6vw, 3.9rem);
          font-weight: 600;
          line-height: 1.08;
          letter-spacing: -0.015em;
          color: #18160F;
          margin: 1.15rem 0 1.3rem;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.7s ease 0.12s, transform 0.7s ease 0.12s;
        }

        .bh-hero.is-in .bh-title {
          opacity: 1;
          transform: translateY(0);
        }

        .bh-title-rotator {
          position: relative;
          display: inline-flex;
          vertical-align: bottom;
          overflow: hidden;
          height: 1.12em;
          padding-bottom: 0.05em;
        }

        .bh-rotator-track {
          display: flex;
          flex-direction: column;
          transition: transform 0.55s cubic-bezier(0.65, 0, 0.35, 1);
        }

        .bh-rotator-word {
          height: 1.12em;
          line-height: 1.12em;
          font-style: italic;
          color: #9A6B0C;
          white-space: nowrap;
        }

        .bh-subtitle {
          font-size: 1.05rem;
          line-height: 1.68;
          color: #5C5848;
          max-width: 500px;
          margin-bottom: 2.1rem;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 0.7s ease 0.26s, transform 0.7s ease 0.26s;
        }

        .bh-hero.is-in .bh-subtitle {
          opacity: 1;
          transform: translateY(0);
        }

        .bh-ctas {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2.4rem;
          flex-wrap: wrap;
          opacity: 0;
          transform: translateY(14px);
          transition: opacity 0.7s ease 0.36s, transform 0.7s ease 0.36s;
        }

        .bh-hero.is-in .bh-ctas {
          opacity: 1;
          transform: translateY(0);
        }

        .bh-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.82rem 1.6rem;
          background: #18160F;
          color: #FBF8F2;
          font-size: 0.88rem;
          font-weight: 600;
          border-radius: 0.5rem;
          text-decoration: none;
          border: 1px solid #18160F;
          transition: transform 0.25s cubic-bezier(0.16,1,0.3,1), background 0.25s ease;
        }

        .bh-btn-primary:hover {
          background: #322E1E;
          transform: translateY(-2px);
        }

        .bh-btn-ghost {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.8rem 1.5rem;
          background: transparent;
          color: #18160F;
          font-size: 0.88rem;
          font-weight: 600;
          border-radius: 0.5rem;
          border: 1px solid rgba(24,22,18,0.16);
          text-decoration: none;
          transition: border-color 0.25s ease, transform 0.25s cubic-bezier(0.16,1,0.3,1);
        }

        .bh-btn-ghost:hover {
          border-color: rgba(24,22,18,0.35);
          transform: translateY(-2px);
        }

        .bh-trust-row {
          display: flex;
          align-items: center;
          gap: 1.6rem;
          flex-wrap: wrap;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 0.7s ease 0.46s, transform 0.7s ease 0.46s;
        }

        .bh-hero.is-in .bh-trust-row {
          opacity: 1;
          transform: translateY(0);
        }

        .bh-trust-item {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.82rem;
          font-weight: 600;
          color: #5C5848;
        }

        .bh-trust-item i {
          color: #B8860B;
          font-size: 0.85rem;
        }

        .bh-trust-divider {
          width: 1px;
          height: 16px;
          background: rgba(24,22,18,0.14);
        }

        .bh-visual-col {
          flex: 1;
          opacity: 0;
          transform: translateY(20px) scale(0.96);
          transition: opacity 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s, transform 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s;
        }

        .bh-hero.is-in .bh-visual-col {
          opacity: 1;
          transform: translateY(0) scale(1);
        }

        .bh-card {
          position: relative;
          background: #FFFFFF;
          border: 1px solid rgba(24,22,18,0.08);
          border-radius: 1.25rem;
          padding: 1.5rem;
          box-shadow: 0 30px 70px rgba(24,22,18,0.1), 0 6px 18px rgba(24,22,18,0.05);
        }

        .bh-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.3rem;
        }

        .bh-card-title {
          font-size: 0.86rem;
          font-weight: 700;
          color: #18160F;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .bh-card-title i {
          color: #B8860B;
        }

        .bh-card-period {
          font-size: 0.74rem;
          font-weight: 600;
          color: #9A958A;
          background: #F3EFE5;
          padding: 0.25rem 0.65rem;
          border-radius: 999px;
        }

        .bh-card-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.7rem;
          margin-bottom: 1.4rem;
        }

        .bh-card-stat {
          background: #FAF8F2;
          border: 1px solid rgba(24,22,18,0.06);
          border-radius: 0.75rem;
          padding: 0.85rem 0.7rem;
        }

        .bh-card-stat-val {
          font-size: 1.2rem;
          font-weight: 800;
          color: #18160F;
          letter-spacing: -0.01em;
        }

        .bh-card-stat-lbl {
          font-size: 0.68rem;
          color: #9A958A;
          font-weight: 600;
          margin-top: 0.2rem;
        }

        .bh-card-chart-label {
          font-size: 0.72rem;
          font-weight: 700;
          color: #9A958A;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 0.65rem;
        }

        .bh-card-bars {
          display: flex;
          align-items: flex-end;
          gap: 0.5rem;
          height: 72px;
          margin-bottom: 1.3rem;
        }

        .bh-card-bar-wrap {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.4rem;
          height: 100%;
          justify-content: flex-end;
        }

        .bh-card-bar {
          width: 100%;
          border-radius: 4px 4px 0 0;
          background: #E9E2D0;
          transition: height 1s cubic-bezier(0.16,1,0.3,1);
        }

        .bh-card-bar.active {
          background: linear-gradient(180deg, #C9981E 0%, #B8860B 100%);
        }

        .bh-card-bar-month {
          font-size: 0.62rem;
          color: #9A958A;
          font-weight: 600;
        }

        .bh-card-txns {
          border-top: 1px solid rgba(24,22,18,0.07);
          padding-top: 0.9rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }

        .bh-card-txn {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.78rem;
        }

        .bh-card-txn-name {
          color: #18160F;
          font-weight: 600;
        }

        .bh-card-badge {
          font-size: 0.66rem;
          font-weight: 700;
          padding: 0.18rem 0.55rem;
          border-radius: 999px;
          text-transform: capitalize;
        }

        .bh-card-badge.paid {
          background: #E3F3EA;
          color: #1B7A43;
        }

        .bh-card-badge.pending {
          background: #FBEFD8;
          color: #9A6B0C;
        }

        .bh-card-txn-amt {
          color: #5C5848;
          font-weight: 600;
        }

        .bh-float-chip {
          position: absolute;
          background: #18160F;
          color: #FBF8F2;
          padding: 0.6rem 0.95rem;
          border-radius: 0.75rem;
          font-size: 0.78rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          box-shadow: 0 12px 28px rgba(24,22,18,0.22);
        }

        .bh-float-chip-1 {
          top: -14px;
          right: 18px;
        }

        .bh-float-chip-1 i {
          color: #4ADE80;
        }

        .bh-float-chip-2 {
          bottom: -14px;
          left: 18px;
          background: #FFFFFF;
          color: #18160F;
          border: 1px solid rgba(24,22,18,0.08);
        }

        .bh-float-chip-2 i {
          color: #B8860B;
        }

        @media (max-width: 1024px) {
          .bh-inner { gap: 3rem; }
          .bh-hairline-frame { inset: 1.25rem; }
        }

        @media (max-width: 768px) {
          .bh-hero {
            padding: 6rem 1.25rem 3.5rem;
          }

          .bh-inner {
            flex-direction: column;
            gap: 3rem;
          }

          .bh-text-col {
            text-align: center;
          }

          .bh-ctas, .bh-trust-row {
            justify-content: center;
          }

          .bh-subtitle {
            margin-left: auto;
            margin-right: auto;
          }

          .bh-float-chip {
            font-size: 0.7rem;
            padding: 0.5rem 0.8rem;
          }

          .bh-card-stats {
            gap: 0.5rem;
          }
        }

        @media (max-width: 480px) {
          .bh-ctas {
            flex-direction: column;
            width: 100%;
          }

          .bh-btn-primary, .bh-btn-ghost {
            width: 100%;
            justify-content: center;
          }

          .bh-card-stat-val {
            font-size: 1.02rem;
          }

          .bh-float-chip-1 {
            top: -10px;
            right: 10px;
          }

          .bh-float-chip-2 {
            bottom: -10px;
            left: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .bh-kicker, .bh-title, .bh-subtitle, .bh-ctas, .bh-trust-row, .bh-visual-col {
            opacity: 1 !important;
            transform: none !important;
            transition: none !important;
          }
        }
      `}</style>

      <div className="bh-hairline-frame"></div>
      <div className="bh-glow bh-glow-1"></div>
      <div className="bh-glow bh-glow-2"></div>

      <div className="bh-inner">
        <div className="bh-text-col">
          <span className="bh-kicker">
            <span className="bh-kicker-mark"></span>
            Bellary Info Tech Solutions
          </span>

          <h1 className="bh-title">
            Transforming businesses with<br />
            premium{' '}
            <span className="bh-title-rotator">
              <span
                className="bh-rotator-track"
                style={{ transform: `translateY(-${wordIndex * 1.12}em)` }}
              >
                {rotatingWords.map((w, i) => (
                  <span key={i} className="bh-rotator-word">{w}</span>
                ))}
              </span>
            </span>
          </h1>

          <p className="bh-subtitle">
            We design and engineer web, mobile, and billing platforms that drive growth,
            cut operational cost, and keep your business ahead — built with the precision
            of a craft, not a commodity.
          </p>

          <div className="bh-ctas">
            <Link to="/service-bits" className="bh-btn-primary">
              Explore Services <i className="bi bi-arrow-right"></i>
            </Link>
            <Link to="/contact-bits#contact-form" className="bh-btn-ghost">
              Get a Quote
            </Link>
          </div>

          <div className="bh-trust-row">
            <span className="bh-trust-item"><i className="bi bi-patch-check-fill"></i> 10+ Projects Delivered</span>
            <span className="bh-trust-divider"></span>
            <span className="bh-trust-item"><i className="bi bi-patch-check-fill"></i> 10+ Happy Clients</span>
          </div>
        </div>

        <div className="bh-visual-col">
          <div className="bh-card">
            <div className="bh-card-top">
              <span className="bh-card-title"><i className="bi bi-graph-up-arrow"></i> Billing Dashboard</span>
              <span className="bh-card-period">Jun 2026</span>
            </div>

            <div className="bh-card-stats">
              <div className="bh-card-stat">
                <div className="bh-card-stat-val">₹{revenue}L</div>
                <div className="bh-card-stat-lbl">Revenue</div>
              </div>
              <div className="bh-card-stat">
                <div className="bh-card-stat-val">48</div>
                <div className="bh-card-stat-lbl">Clients</div>
              </div>
              <div className="bh-card-stat">
                <div className="bh-card-stat-val">7</div>
                <div className="bh-card-stat-lbl">Pending</div>
              </div>
            </div>

            <div className="bh-card-chart-label">Monthly Revenue</div>
            <div className="bh-card-bars">
              {[
                { h: 38, m: 'Jan' },
                { h: 50, m: 'Feb' },
                { h: 42, m: 'Mar' },
                { h: 60, m: 'Apr' },
                { h: 53, m: 'May' },
                { h: 72, m: 'Jun' },
              ].map((b, i) => (
                <div key={b.m} className="bh-card-bar-wrap">
                  <div
                    className={`bh-card-bar ${i === 5 ? 'active' : ''}`}
                    style={{ height: inView ? `${b.h}px` : '0px' }}
                  ></div>
                  <span className="bh-card-bar-month">{b.m}</span>
                </div>
              ))}
            </div>

            <div className="bh-card-txns">
              <div className="bh-card-txn">
                <span className="bh-card-txn-name">Kalyani Traders</span>
                <span className="bh-card-badge paid">paid</span>
                <span className="bh-card-txn-amt">₹45,000</span>
              </div>
              <div className="bh-card-txn">
                <span className="bh-card-txn-name">Deccan Infoworks</span>
                <span className="bh-card-badge paid">paid</span>
                <span className="bh-card-txn-amt">₹72,000</span>
              </div>
            </div>

            <div className="bh-float-chip bh-float-chip-1">
              <i className="bi bi-check-circle-fill"></i> Invoice Paid
            </div>
            <div className="bh-float-chip bh-float-chip-2">
              <i className="bi bi-shield-check"></i> GST Compliant
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Carouselnew;